import {
  WS_EVENTS,
  type WsConnectivityPayload,
  type WsErrorPayload,
  type WsSnapshotPayload,
  type WsVehicleUpdatePayload,
} from '@fleetcore/shared/telemetry'
import { io } from 'socket.io-client'

import { getAccessToken } from '../auth/token-storage'
import { env } from '../env'
import { refreshAccessToken } from './api-client'

/**
 * The live transport.
 *
 * Two rules from the gateway's contract shape everything here:
 *
 *  - THE TENANT IS NEVER SENT. `live:subscribe_tenant` takes no payload; the room the
 *    socket joins is named from the `tid` claim of the token presented at the
 *    handshake. There is deliberately no code path in this file that could put a
 *    tenant id on the wire, because a client-supplied one is a cross-tenant vector by
 *    construction — see socket-auth.ts on the server.
 *
 *  - THE TOKEN GOES IN `auth`, NEVER IN THE QUERY STRING. engine.io's polling
 *    transport repeats the query on every HTTP request, which lands a bearer
 *    credential in access logs, `Referer` headers and every proxy in between.
 *
 * An access token authenticates the socket once and then the socket outlives it, so
 * the server sweeps expired sockets every 30 s and refuses the handshake with
 * `token_expired`. That is not an error to show a user: it is the signal to rotate the
 * pair through the same single-flight refresh the HTTP client uses and reconnect.
 */

export type LiveSocketStatus = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'polling'

/**
 * Rejections the gateway sends as `connect_error` → `err.data.code`. `token_expired`
 * is recoverable and handled here; the others mean this session will never open a
 * socket, so the map falls back to polling rather than retrying forever.
 */
const FATAL_HANDSHAKE_CODES = new Set(['invalid_token', 'tenant_context_required'])

/**
 * After this many consecutive failures the map stops pretending a socket is coming
 * and says so. socket.io keeps trying underneath — a network that returns reconnects
 * on its own — but the indicator must not stay on "reconnecting" for an hour, because
 * a map that looks live and is not is worse than one that admits it is stale.
 */
const MAX_SILENT_RETRIES = 4

export interface LiveSocketHandlers {
  onSnapshot?: (payload: WsSnapshotPayload) => void
  onVehicleUpdate?: (payload: WsVehicleUpdatePayload) => void
  onConnectivity?: (payload: WsConnectivityPayload) => void
  onEvent?: (payload: unknown) => void
  onServerError?: (payload: WsErrorPayload) => void
  onStatus?: (status: LiveSocketStatus, errorCode: string | null) => void
}

/**
 * The slice of `socket.io-client`'s Socket this module uses.
 *
 * Narrow on purpose: it is also the surface a test double has to implement, and a
 * double that has to mirror the whole Socket type is a double nobody writes.
 */
export interface SocketLike {
  auth: Record<string, unknown>
  readonly connected: boolean
  on: (event: string, handler: (...args: never[]) => void) => unknown
  off: (event: string, handler?: (...args: never[]) => void) => unknown
  emit: (event: string, ...args: unknown[]) => unknown
  connect: () => unknown
  disconnect: () => unknown
  removeAllListeners: () => unknown
}

export interface SocketOptions {
  auth: { token: string }
  path: string
  transports: string[]
  autoConnect: boolean
  withCredentials: boolean
  reconnectionDelay: number
  reconnectionDelayMax: number
}

export type SocketFactory = (url: string, options: SocketOptions) => SocketLike

/** Matches the gateway's `WS_PATH`, which sits outside `/api/v1` on purpose. */
export const SOCKET_PATH = '/socket.io'

const defaultFactory: SocketFactory = (url, options) => io(url, options) as unknown as SocketLike

export interface LiveSocketOptions {
  url?: string
  factory?: SocketFactory
  handlers: LiveSocketHandlers
  /** Test seam only. Production always rotates through the shared single flight. */
  refreshToken?: () => Promise<string | null>
  readToken?: () => string | null
}

export class LiveSocket {
  private socket: SocketLike | null = null
  private readonly followed = new Set<string>()
  private status: LiveSocketStatus = 'idle'
  private failures = 0
  private stopped = true
  /**
   * Set when the server refused the handshake for a reason retrying cannot fix.
   *
   * Without it the `disconnect` that follows the refusal would overwrite `polling`
   * with `reconnecting`, and the map would promise a socket that is never coming —
   * the precise failure this whole indicator exists to prevent.
   */
  private fatal = false

  private readonly url: string
  private readonly factory: SocketFactory
  private readonly handlers: LiveSocketHandlers
  private readonly refreshToken: () => Promise<string | null>
  private readonly readToken: () => string | null

  constructor(options: LiveSocketOptions) {
    this.url = options.url ?? env.wsUrl
    this.factory = options.factory ?? defaultFactory
    this.handlers = options.handlers
    this.refreshToken = options.refreshToken ?? refreshAccessToken
    this.readToken = options.readToken ?? getAccessToken
  }

  get currentStatus(): LiveSocketStatus {
    return this.status
  }

  start(): void {
    if (this.socket !== null) return
    this.stopped = false
    this.fatal = false

    const token = this.readToken()
    if (token === null) {
      // Nothing to authenticate with. Not an error — the shell is still restoring the
      // session — so the caller polls and `start()` is called again once it lands.
      this.emitStatus('polling', 'missing_token')
      return
    }

    this.emitStatus('connecting', null)
    const socket = this.factory(this.url, {
      auth: { token },
      path: SOCKET_PATH,
      // WebSocket first. Polling still works as a fallback inside socket.io, but a
      // fleet map opening with an HTTP long-poll wastes a round trip on every corporate
      // network that does support upgrades, which is nearly all of them.
      transports: ['websocket', 'polling'],
      autoConnect: true,
      withCredentials: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 10_000,
    })

    this.socket = socket
    this.bind(socket)
  }

  stop(): void {
    this.stopped = true
    this.followed.clear()
    const socket = this.socket
    this.socket = null
    if (socket !== null) {
      socket.removeAllListeners()
      socket.disconnect()
    }
    this.failures = 0
    this.emitStatus('idle', null)
  }

  /** Joins the per-vehicle room so a detail panel gets its vehicle's every record. */
  follow(vehicleId: string): void {
    if (this.followed.has(vehicleId)) return
    this.followed.add(vehicleId)
    if (this.socket?.connected === true) {
      this.socket.emit(WS_EVENTS.subscribeVehicle, { vehicleId })
    }
  }

  unfollow(vehicleId: string): void {
    if (!this.followed.delete(vehicleId)) return
    if (this.socket?.connected === true) {
      this.socket.emit(WS_EVENTS.unsubscribeVehicle, { vehicleId })
    }
  }

  private bind(socket: SocketLike): void {
    socket.on('connect', (() => {
      this.failures = 0
      this.fatal = false
      this.emitStatus('live', null)
      // No payload: the tenant comes from the token. Re-sent on every reconnect
      // because a reconnected socket is a new socket in a new room set.
      socket.emit(WS_EVENTS.subscribeTenant)
      for (const vehicleId of this.followed) {
        socket.emit(WS_EVENTS.subscribeVehicle, { vehicleId })
      }
    }) as (...args: never[]) => void)

    socket.on('disconnect', ((reason: string) => {
      if (this.stopped || this.fatal) return
      /**
       * `io server disconnect` is the server having closed the socket deliberately —
       * an expired-token sweep, or a shutdown. socket.io does NOT reconnect on its
       * own after that one, so it has to be driven from here or the map silently
       * stops updating while showing "reconnecting" forever.
       */
      this.emitStatus('reconnecting', null)
      if (reason === 'io server disconnect') void this.reconnectWithFreshToken()
    }) as unknown as (...args: never[]) => void)

    socket.on('connect_error', ((error: Error & { data?: { code?: string } }) => {
      if (this.stopped) return
      const code = error.data?.code ?? null
      this.failures += 1

      if (code === 'token_expired' || code === 'missing_token') {
        void this.reconnectWithFreshToken()
        return
      }
      if (code !== null && FATAL_HANDSHAKE_CODES.has(code)) {
        // Retrying cannot help: this session is not permitted to open a socket.
        this.fatal = true
        this.emitStatus('polling', code)
        socket.disconnect()
        return
      }
      this.emitStatus(this.failures >= MAX_SILENT_RETRIES ? 'polling' : 'reconnecting', code)
    }) as unknown as (...args: never[]) => void)

    socket.on(WS_EVENTS.snapshot, ((payload: WsSnapshotPayload) => {
      this.handlers.onSnapshot?.(payload)
    }) as unknown as (...args: never[]) => void)

    socket.on(WS_EVENTS.vehicleUpdate, ((payload: WsVehicleUpdatePayload) => {
      this.handlers.onVehicleUpdate?.(payload)
    }) as unknown as (...args: never[]) => void)

    socket.on(WS_EVENTS.vehicleConnectivity, ((payload: WsConnectivityPayload) => {
      this.handlers.onConnectivity?.(payload)
    }) as unknown as (...args: never[]) => void)

    socket.on(WS_EVENTS.event, ((payload: unknown) => {
      this.handlers.onEvent?.(payload)
    }) as unknown as (...args: never[]) => void)

    socket.on(WS_EVENTS.error, ((payload: WsErrorPayload) => {
      this.handlers.onServerError?.(payload)
    }) as unknown as (...args: never[]) => void)
  }

  /**
   * Rotates the token and reconnects the same socket.
   *
   * Through `refreshAccessToken`, which is the single-flight the HTTP client already
   * owns: a map open beside four queries whose access token expired at the same second
   * must produce one rotation, not five. Five would look to the server like a replayed
   * refresh family and sign the user out for the crime of leaving a tab open.
   */
  private async reconnectWithFreshToken(): Promise<void> {
    const socket = this.socket
    if (socket === null || this.stopped) return

    const token = await this.refreshToken()
    if (this.stopped || this.socket !== socket) return

    if (token === null) {
      // The session itself is over; `notifySessionEnded` has already fired and the
      // shell is on its way to the login screen. Nothing to reconnect to.
      this.emitStatus('polling', 'session_expired')
      return
    }

    socket.auth = { token }
    socket.connect()
  }

  private emitStatus(status: LiveSocketStatus, errorCode: string | null): void {
    if (this.status === status) return
    this.status = status
    this.handlers.onStatus?.(status, errorCode)
  }
}
